import type { AnchorHTMLAttributes, ButtonHTMLAttributes, ReactNode } from 'react';
import { cn } from '@/lib/format';

/* ---------------------------------------------------------------------------
   Primitives shared by every section. Square corners, hairline borders, orange
   reserved for the single most important action on screen (brand book §3.5).
   ------------------------------------------------------------------------ */

type ButtonVariant = 'primary' | 'secondary' | 'ghost' | 'outline';
type ButtonSize = 'sm' | 'md' | 'lg';

const VARIANTS: Record<ButtonVariant, string> = {
  primary:
    'bg-accent text-on-accent hover:bg-accent-hover active:translate-y-px shadow-[0_1px_0_0_rgba(17,17,17,0.2)]',
  secondary: 'bg-brand-charcoal text-brand-white hover:opacity-90 active:translate-y-px',
  ghost: 'bg-transparent text-ink hover:bg-page-subtle',
  outline: 'bg-transparent text-ink border border-hairline hover:border-accent hover:text-accent',
};

const SIZES: Record<ButtonSize, string> = {
  sm: 'h-9 px-3 text-[12px] tracking-[0.08em]',
  md: 'h-11 px-5 text-[13px] tracking-[0.08em]',
  lg: 'h-14 px-7 text-[14px] tracking-[0.08em]',
};

const BASE =
  'inline-flex items-center justify-center gap-2 font-bold uppercase select-none transition-all duration-150 rounded-none disabled:opacity-40 disabled:pointer-events-none';

export interface ButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: ButtonVariant;
  size?: ButtonSize;
  children: ReactNode;
}

export function Button({ variant = 'primary', size = 'md', className, children, ...rest }: ButtonProps) {
  return (
    <button type="button" className={cn(BASE, VARIANTS[variant], SIZES[size], className)} {...rest}>
      {children}
    </button>
  );
}

export interface LinkButtonProps extends AnchorHTMLAttributes<HTMLAnchorElement> {
  variant?: ButtonVariant;
  size?: ButtonSize;
  children: ReactNode;
}

export function LinkButton({ variant = 'primary', size = 'md', className, children, ...rest }: LinkButtonProps) {
  return (
    <a className={cn(BASE, VARIANTS[variant], SIZES[size], className)} {...rest}>
      {children}
    </a>
  );
}

export function Card({
  children,
  className,
  shape = 'card',
  as: Tag = 'div',
}: {
  children: ReactNode;
  className?: string;
  /**
   * `chat` uses the chat's softer shape language (22px, floating shadow) — the
   * chat surfaces are their own place, not another page of 8px cards.
   */
  shape?: 'card' | 'chat';
  as?: 'div' | 'section' | 'article' | 'li';
}) {
  return (
    <Tag
      className={cn(
        'border border-hairline bg-surface',
        shape === 'chat' ? 'rounded-chat-lg shadow-float' : 'rounded-card shadow-card',
        className,
      )}
    >
      {children}
    </Tag>
  );
}

export function Eyebrow({ children, className }: { children: ReactNode; className?: string }) {
  return (
    <p className={cn('text-label font-bold uppercase tracking-[0.08em] text-accent', className)}>{children}</p>
  );
}

export function SectionHeading({
  eyebrow,
  title,
  lead,
  align = 'left',
  className,
}: {
  eyebrow?: string;
  title: ReactNode;
  lead?: ReactNode;
  align?: 'left' | 'center';
  className?: string;
}) {
  return (
    <div className={cn('flex flex-col gap-4', align === 'center' && 'items-center text-center', className)}>
      {eyebrow ? <Eyebrow>{eyebrow}</Eyebrow> : null}
      <h2 className="font-display text-h2 font-black uppercase leading-tight tracking-[-0.01em] text-ink md:text-display-2">
        {title}
      </h2>
      {lead ? <p className="max-w-[62ch] text-body-lg text-ink-muted">{lead}</p> : null}
    </div>
  );
}

export function Pill({
  children,
  tone = 'neutral',
  className,
}: {
  children: ReactNode;
  tone?: 'neutral' | 'accent' | 'success' | 'warning' | 'danger' | 'info';
  className?: string;
}) {
  const tones: Record<string, string> = {
    neutral: 'border-hairline text-ink-muted',
    accent: 'border-accent bg-accent/10 text-accent',
    success: 'border-success bg-success/10 text-success',
    warning: 'border-warning bg-warning/10 text-warning',
    danger: 'border-danger bg-danger/10 text-danger',
    info: 'border-info bg-info/10 text-info',
  };
  return (
    <span
      className={cn(
        'inline-flex items-center gap-1 rounded-pill border px-2.5 py-1 text-[11px] font-bold uppercase tracking-[0.08em]',
        tones[tone],
        className,
      )}
    >
      {children}
    </span>
  );
}

export function Stat({
  label,
  value,
  hint,
  tone = 'default',
}: {
  label: string;
  value: ReactNode;
  hint?: ReactNode;
  tone?: 'default' | 'accent';
}) {
  return (
    <div className="flex min-w-0 flex-col gap-1">
      <span className="text-[11px] font-bold uppercase tracking-[0.1em] text-ink-muted">{label}</span>
      <span
        className={cn(
          'l2f-tabular font-mono text-2xl font-bold leading-none',
          tone === 'accent' ? 'text-accent' : 'text-ink',
        )}
      >
        {value}
      </span>
      {hint ? <span className="text-[12px] text-ink-muted">{hint}</span> : null}
    </div>
  );
}

export function Divider({ className }: { className?: string }) {
  return <hr className={cn('border-0 border-t border-hairline', className)} />;
}
